import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { usePublicPlans } from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { Pill } from "@/components/ui/Pill";
import { useReveal } from "@/components/platform-landing/useReveal";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import {
  entitlementAvailability,
  limitAvailability,
  type Availability,
} from "@/lib/entitlementAvailability";
import { useSeo } from "@/lib/seo";
import { featureShot } from "@/marketing/featureShotSrc";

import "./CapabilityPage.css";

/**
 * One page of the „Co umíme“ section: what a shop owner stops doing by
 * hand, told in their words, with the screens it happens on.
 *
 * The section exists because the public site sold quoting to the depth
 * of a whole website and the other half of the product to the depth of
 * a footnote — the landing's "a další — sklad, šarže, štítky" was the
 * entire pitch for it. Five pages share this component so the five read
 * as one section rather than five people's idea of a feature page.
 *
 * **Every page is written in "vy".** The section is called „Co umíme“
 * and that is the only place the product speaks about itself; from the
 * H1 down the subject is the reader's workshop, not us.
 *
 * **The plan badge is read, never written.** A sentence that says
 * "from Business up" survives the repricing that makes it false: an
 * admin moves an entitlement in ``/admin/plans``, the page keeps the
 * old tier name and nothing fails. So an item names its entitlement key
 * and the tier comes from ``GET /public/plans`` at request time, via
 * the same helper the security page uses.
 *
 * The badge renders in exactly one case — ``kind: "from"``. The other
 * three say nothing worth a badge and get none:
 *
 * * **no key at all** — the item is not gated by a plan. This is the
 *   majority: most items in the section carry no key, because the
 *   warehouse, purchasing, the board, invoices and the embedded
 *   configurator are on every tier. A whole page with no badge is the
 *   argument, not a gap in the data.
 * * ``kind: "all"`` — every published tier grants it, so "from which
 *   plan" is not a question the reader has.
 * * ``kind: "none"`` — no published plan grants it. That happens for
 *   real: the catalogue only publishes ``is_public=true`` tiers, and on
 *   a fresh install (CI, the prerender capture) ``enterprise`` is
 *   seeded ``is_public: False``, so keys only it grants resolve to
 *   nothing. Silence is the honest answer; a blank badge would read as
 *   a tier name that failed to load.
 */

/** One claim on the page. ``entitlement`` omitted ⇒ it is on every plan. */
export interface CapabilityItem {
  /** Key under ``<i18nKey>.sections.<section>.items`` — title + body. */
  id: string;
  /** Entitlement key from ``GET /public/plans``. Omit for ungated items. */
  entitlement?: string;
  /**
   * A plan limit instead of a switch — the key of a ``metrics`` row of
   * ``GET /public/plans`` (e.g. ``max_api_tokens``). The badge names the
   * cheapest plan whose limit is above zero, the same row the pricing
   * table shows (KAN-1949). Used when the capability is a count, not a
   * key; never together with ``entitlement``.
   */
  limit?: string;
  /**
   * Spans the whole grid row and sets in larger type — the section's
   * lede. At most one per section, and only where one claim genuinely
   * carries the rest; used on every card it would be a wider card, not
   * an emphasis.
   */
  wide?: boolean;
}

export interface CapabilitySection {
  /** Key under ``<i18nKey>.sections`` — heading and optional lead. */
  id: string;
  items: readonly CapabilityItem[];
  /**
   * A screen this section happens on — the id of a still made by
   * ``scripts/feature_shots.py`` (KAN-1950). The file is per language
   * (``public/features/<lang>/<id>.webp``) because a Czech screen on the
   * English page would say the product does not speak English. The alt
   * text is authored next to the copy, at
   * ``<i18nKey>.sections.<id>.shotAlt``. An id the manifest does not
   * know renders nothing rather than a broken image.
   */
  shot?: string;
  /**
   * ``"ink"`` sets the section on the near-black block the landing uses
   * for the things it wants read rather than skimmed. One per page at
   * most: the contrast is the point, and a second one spends it.
   */
  tone?: "ink";
  /**
   * ``"it"`` labels the section „Pro firmy s vlastním IT“ in its kicker.
   * For sections a shop owner cannot set up without somebody technical
   * (API, company sign-in): the 2026-10-07 audit (#68) found them sitting
   * between the owner's own sections with nothing saying they can be
   * skipped. The label is the warning; the cards still say what it buys.
   */
  audience?: "it";
}

export interface CapabilityPageProps {
  /** i18n prefix in the ``saas`` namespace, e.g. ``features.pages.warehouse``. */
  i18nKey: string;
  /** Route path without the lang prefix — the SEO canonical. */
  path: string;
  /** Three sentences about the day the reader has now, before the product. */
  pains: readonly string[];
  /**
   * What changes, answering ``pains`` one for one. Optional because the
   * first page shipped without it, not because a page is better off
   * silent: a list of problems that nothing closes reads as a complaint
   * about the reader's week. The owner asked for the payoff to be said
   * out loud (2026-09-18), so the sentences here are the reader's gain
   * — never a restatement of what the product does.
   */
  gains?: readonly string[];
  sections: readonly CapabilitySection[];
  /**
   * Where the page stops. Written where a reader would otherwise assume
   * more than the product does — see the invoicing page's "this is not
   * an ERP". Omitted when there is nothing to disclaim.
   */
  hasBoundary?: boolean;
  /**
   * A block between the reader's week and the sections, for a page whose
   * sections lean on a word the reader does not know yet — the
   * production page's "map" (KAN-1950). Explaining it in the first card
   * that uses it would bury the explanation in one of twelve cards.
   */
  explainer?: ReactNode;
}

export function CapabilityPage({
  i18nKey,
  path,
  pains,
  gains,
  sections,
  hasBoundary = false,
  explainer,
}: CapabilityPageProps): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const reveal = useReveal<HTMLDivElement>();
  const plans = usePublicPlans();
  const planCards = plans.data?.items ?? [];
  const planMetrics = plans.data?.metrics ?? [];

  useSeo({
    title: t(`${i18nKey}.seo.title`),
    description: t(`${i18nKey}.seo.description`),
    path,
    lang,
  });

  /** ``null`` ⇒ render no badge. See the module docstring for why. */
  const planLabel = (item: CapabilityItem): string | null => {
    let availability: Availability;
    if (item.limit) {
      availability = limitAvailability(planCards, planMetrics, item.limit);
    } else if (item.entitlement) {
      availability = entitlementAvailability(planCards, item.entitlement);
    } else {
      return null;
    }
    if (availability.kind !== "from") return null;
    return t("features.planBadge", {
      plan: pickI18nBlob(availability.plan.name_i18n, availability.plan.name, availability.plan.name_en, lang),
    });
  };

  return (
    <div className="cp" ref={reveal} data-testid="capability-page">
      <header className="hero">
        <div className="inner">
          <p className="eyebrow">
            <span className="dot" aria-hidden="true" />
            {t(`${i18nKey}.eyebrow`)}
          </p>
          <h1 className="h1">{t(`${i18nKey}.title`)}</h1>
          <p className="lead">{t(`${i18nKey}.lead`)}</p>
        </div>
      </header>

      {/* The day before the product, and the day after it. Three
          sentences each, side by side: a reader who does not recognise
          their own week on the left will not read the rest, and a list
          of problems that nothing closes reads as a complaint about
          that week. */}
      <section className="section" data-testid="capability-pains">
        <div className="inner">
          <div className="band">
            <div className="col">
              <h3>{t(`${i18nKey}.pains.heading`)}</h3>
              <ul>
                {pains.map((id) => (
                  <li key={id} data-testid={`capability-pain-${id}`}>
                    {t(`${i18nKey}.pains.items.${id}`)}
                  </li>
                ))}
              </ul>
            </div>
            {gains && gains.length > 0 && (
              <div className="col gain">
                <h3>{t(`${i18nKey}.gains.heading`)}</h3>
                <ul>
                  {gains.map((id) => (
                    <li key={id} data-testid={`capability-gain-${id}`}>
                      {t(`${i18nKey}.gains.items.${id}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {explainer}

      {sections.map((section, index) => (
        <section
          key={section.id}
          className={
            section.tone === "ink"
              ? "section ink"
              : index % 2 === 1
                ? "section alt"
                : "section"
          }
          data-testid={`capability-section-${section.id}`}
        >
          <div className="inner">
            {/* Number and rule; the counter lives in CSS so the sequence
                cannot drift from the order sections render in, and no
                translator has to keep "01" in step. A section for
                somebody technical says so here, after the rule. */}
            {section.audience === "it" ? (
              <p className="kicker" data-testid={`capability-audience-${section.id}`}>
                <span className="audience">{t("features.audienceIt")}</span>
              </p>
            ) : (
              <p className="kicker" aria-hidden="true" />
            )}
            <h2 className="h2">{t(`${i18nKey}.sections.${section.id}.heading`)}</h2>
            {/* An empty string is what the resolver returns for a key
                nobody authored; a section without a lead should render
                no paragraph rather than an empty one. */}
            {t(`${i18nKey}.sections.${section.id}.lead`, "") && (
              <p className="lead">{t(`${i18nKey}.sections.${section.id}.lead`)}</p>
            )}
            {/* Columns follow the cards that actually sit in the grid:
                a ``wide`` one spans the row, so three of the rest fill a
                row exactly and anything else pairs. Counting all items
                instead leaves a hole where the grid's 1px gap shows
                through as a stray block. */}
            <ul
              className={
                section.items.filter((i) => !i.wide).length === 3
                  ? "feats cols-3"
                  : "feats"
              }
            >
              {section.items.map((item) => {
                const badge = planLabel(item);
                // A lone card in a two-column grid leaves the other half
                // as an empty grey block — the grid's 1px gap colour
                // showing through (KAN-1950, audit #60). Alone, it is the
                // section's lede anyway.
                const wide = item.wide || section.items.length === 1;
                return (
                  <li
                    key={item.id}
                    className={wide ? "feat wide" : "feat"}
                    data-testid={`capability-item-${item.id}`}
                  >
                    <h3>
                      {t(`${i18nKey}.sections.${section.id}.items.${item.id}.title`)}
                      {badge && (
                        <Pill tone="plan" data-testid={`capability-plan-${item.id}`}>
                          {badge}
                        </Pill>
                      )}
                    </h3>
                    <p>{t(`${i18nKey}.sections.${section.id}.items.${item.id}.body`)}</p>
                  </li>
                );
              })}
            </ul>
            <SectionShot
              id={section.shot}
              lang={lang}
              alt={t(`${i18nKey}.sections.${section.id}.shotAlt`, "")}
              testId={`capability-shot-${section.id}`}
            />
          </div>
        </section>
      ))}

      {hasBoundary && (
        <section className="section ink" data-testid="capability-boundary">
          <div className="inner">
            <h2 className="h2">{t(`${i18nKey}.boundary.heading`)}</h2>
            <p className="lead">{t(`${i18nKey}.boundary.body`)}</p>
          </div>
        </section>
      )}

      <section className="final" data-testid="capability-cta">
        <div className="inner">
          <h2 className="h2">{t(`${i18nKey}.finalCta.heading`)}</h2>
          <p className="lead">{t(`${i18nKey}.finalCta.lead`)}</p>
          <div className="btns">
            <Link
              to={`/${lang}/sign-up-shop`}
              className="btn-cta"
              data-testid="capability-cta-primary"
            >
              {t(`${i18nKey}.finalCta.primary`)}
            </Link>
            <Link
              to={`/${lang}/features`}
              className="btn-cta-2"
              data-testid="capability-cta-secondary"
            >
              {t(`${i18nKey}.finalCta.secondary`)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/** One still of the app, or nothing when the id has no file. */
export function SectionShot({
  id,
  lang,
  alt,
  testId,
}: {
  id: string | undefined;
  lang: string;
  alt: string;
  testId: string;
}): JSX.Element | null {
  const shot = id ? featureShot(id, lang) : null;
  if (!shot) return null;
  return (
    <img
      src={shot.src}
      width={shot.width}
      height={shot.height}
      loading="lazy"
      decoding="async"
      alt={alt}
      className="shot"
      style={{ maxWidth: shot.cssWidth }}
      data-testid={testId}
    />
  );
}
